import clsx from "clsx";
import type { DayView, TripLeg } from "@poi-planner/shared";
import { useLegStore } from "../store/legStore";
import { useTripStore } from "../store/tripStore";
import { type PlannerMode, useUIStore } from "../store/uiStore";
import { addDaysToDate } from "../utils/zonedTime";
import { Button } from "./Button";
import { FormSelect } from "./FormFields";
import { PaneTitleButton } from "./PaneToggle";
import { SegmentedControl } from "./SegmentedControl";
import { CalendarAgendaView } from "./calendar/CalendarAgendaView";
import { CalendarMonthView } from "./calendar/CalendarMonthView";
import { CalendarWeekView } from "./calendar/CalendarWeekView";

function formatDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

interface PlannerPaneProps {
  allDayViews: DayView[];
  visibleDayViews: DayView[];
  activeLeg: TripLeg | null;
  onShowOnMap: (poiId: string) => void;
  onCollapse?: () => void;
}

export function PlannerPane({
  allDayViews,
  visibleDayViews,
  activeLeg,
  onShowOnMap,
  onCollapse,
}: PlannerPaneProps) {
  const trip = useTripStore((state) => state.trip);
  const isReadOnly = useTripStore((state) => state.isReadOnly());
  const legs = useLegStore((state) => state.legs);
  const createLeg = useLegStore((state) => state.createLeg);
  const plannerMode = useUIStore((state) => state.plannerMode);
  const setPlannerMode = useUIStore((state) => state.setPlannerMode);
  const plannerFocusDate = useUIStore((state) => state.plannerFocusDate);
  const setPlannerFocusDate = useUIStore((state) => state.setPlannerFocusDate);
  const setActiveLegId = useUIStore((state) => state.setActiveLegId);
  const openLegEditor = useUIStore((state) => state.openLegEditor);

  if (!trip) return null;

  const focusDate = plannerFocusDate ?? allDayViews[0]?.date ?? trip.startDate;
  const focusIndex = allDayViews.findIndex((day) => day.date === focusDate);
  const activeLegIndex = activeLeg
    ? legs.findIndex((leg) => leg.id === activeLeg.id)
    : -1;

  const createFirstLeg = async () => {
    const leg = await createLeg(trip.id, {
      name: "New leg",
      startDate: focusDate,
      endDate: focusDate,
      color: 0,
    });
    if (!leg) return;
    setActiveLegId(leg.id);
    openLegEditor(leg.id);
  };

  return (
    <section
      className="flex min-h-0 min-w-0 flex-1 flex-col bg-page"
      aria-label="Planner"
      data-testid="planner-pane"
    >
      <div className="z-30 flex min-h-13 shrink-0 flex-wrap items-center gap-2 border-b border-border bg-white px-3 py-1.5">
        {/* A plain narrow-layout title yields to the view switcher; a toggle
            keeps its width and lets the switcher wrap instead */}
        <div
          className={clsx(
            "flex flex-1 basis-0 items-center",
            !onCollapse && "min-w-0",
          )}
        >
          <PaneTitleButton
            pane="planner"
            onCollapse={onCollapse}
            subtitle={
              plannerMode === "day"
                ? focusIndex >= 0
                  ? `Day ${focusIndex + 1} of ${allDayViews.length}`
                  : formatDate(focusDate)
                : plannerMode === "leg" && activeLeg
                  ? `${activeLeg.name} · ${activeLeg.startDate}–${activeLeg.endDate}`
                  : `${visibleDayViews.length} day${visibleDayViews.length === 1 ? "" : "s"}`
            }
          />
        </div>

        <SegmentedControl
          aria-label="Planner view"
          value={plannerMode}
          onChange={(mode: PlannerMode) => setPlannerMode(mode)}
          options={[
            { value: "day", label: "Day" },
            { value: "leg", label: "Leg" },
            { value: "all", label: "All days" },
            { value: "calendar", label: "Calendar" },
            { value: "agenda", label: "Agenda" },
          ]}
        />

        {/* Balances the title so the view switcher stays centred */}
        <div className="flex-1 basis-0" />
      </div>

      {plannerMode === "day" && (
        <div
          className="flex shrink-0 items-center justify-center gap-1 bg-page px-3 pt-3"
          aria-label="Day navigation"
        >
          <Button
            variant="icon"
            icon="chevron_left"
            aria-label="Previous day"
            title="Previous day"
            disabled={focusIndex <= 0}
            onClick={() => setPlannerFocusDate(addDaysToDate(focusDate, -1))}
          />
          <h3 className="m-0 min-w-40 text-center text-sm font-extrabold tracking-tight text-primary">
            {formatDate(focusDate)}
          </h3>
          <Button
            variant="icon"
            icon="chevron_right"
            aria-label="Next day"
            title="Next day"
            disabled={focusIndex < 0 || focusIndex >= allDayViews.length - 1}
            onClick={() => setPlannerFocusDate(addDaysToDate(focusDate, 1))}
          />
        </div>
      )}

      {plannerMode === "leg" && legs.length > 0 && (
        <div
          className="flex shrink-0 items-center justify-center gap-1 bg-page px-3 pt-3"
          aria-label="Leg navigation"
        >
          <Button
            variant="icon"
            icon="chevron_left"
            aria-label="Previous leg"
            title="Previous leg"
            disabled={activeLegIndex <= 0}
            onClick={() => setActiveLegId(legs[activeLegIndex - 1]?.id ?? null)}
          />
          <div>
            <FormSelect
              data-testid="planner-leg-select"
              aria-label="Active leg"
              value={activeLeg?.id ?? legs[0].id}
              onChange={setActiveLegId}
              options={legs.map((leg) => ({
                value: leg.id,
                label: leg.name,
              }))}
            />
          </div>
          <Button
            variant="icon"
            icon="chevron_right"
            aria-label="Next leg"
            title="Next leg"
            disabled={activeLegIndex < 0 || activeLegIndex >= legs.length - 1}
            onClick={() => setActiveLegId(legs[activeLegIndex + 1]?.id ?? null)}
          />
        </div>
      )}

      {plannerMode === "leg" && legs.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <span className="material-symbols-outlined text-4xl text-tertiary">
            route
          </span>
          <div>
            <h3 className="text-sm font-bold text-primary">No legs yet</h3>
            <p className="mt-1 max-w-sm text-xs text-secondary">
              Create a leg to focus the Planner on a named date range.
            </p>
          </div>
          {!isReadOnly && (
            <Button variant="primary" icon="add" onClick={createFirstLeg}>
              Create leg
            </Button>
          )}
        </div>
      ) : plannerMode === "calendar" ? (
        <CalendarMonthView />
      ) : plannerMode === "agenda" ? (
        <CalendarAgendaView dayViews={visibleDayViews} />
      ) : (
        <CalendarWeekView
          dayViews={visibleDayViews}
          contextKey={`${plannerMode}:${visibleDayViews[0]?.date ?? "empty"}:${visibleDayViews.at(-1)?.date ?? "empty"}`}
          onShowOnMap={onShowOnMap}
        />
      )}
    </section>
  );
}
